import React, { useState } from 'react';
import {
  Box,
  Button,
  Input,
  Text,
  HStack,
  Stack,
  Icon,
} from '@chakra-ui/react';
import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogCloseTrigger,
} from '@/components/ui/dialog';
import { Field } from '@chakra-ui/react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FaLock, FaEye, FaEyeSlash } from 'react-icons/fa';
import { Divider } from '@/components/divider';
import { toaster } from '@/components/ui/toaster';
import {
  AuthTemplate,
  AuthType,
  CreateAuthTypeRequest,
} from '../locations.types';

import { z } from 'zod';
import { useCreateLocationAuthType } from '../locations.hook';

const basicAuthFormSchema = z.object({
  name: z.string().min(1, 'Authentication name is required'),
  description: z.string().min(1, 'Description is required'),
  base_url: z
    .string()
    .url('Invalid URL format')
    .optional()
    .default('https://clubplanner.com/api/sales'),
  template: z.literal('basic'),
  username: z.string().min(1, 'Username is required'),
  password: z.string().min(1, 'Password is required'),
  type: z.literal('basic'),
});

type BasicAuthFormData = z.infer<typeof basicAuthFormSchema>;

interface AuthTypeCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: AuthTemplate;
  onSuccess: (authType: AuthType) => void;
}

export const AuthTypeCreateModal: React.FC<AuthTypeCreateModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm<BasicAuthFormData>({
    resolver: zodResolver(basicAuthFormSchema),
    defaultValues: {
      name: '',
      description: '',
      base_url: 'https://clubplanner.com/api/sales',
      template: 'basic',
      username: '',
      password: '',
      type: 'basic',
    },
  });

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
    reset,
  } = form;

  const { mutate: createLocationAuthType } = useCreateLocationAuthType({
    onSuccess: (data) => {
      toaster.create({
        title: 'Authentication Type Created',
        description: `${data.name} has been created successfully.`,
        type: 'success',
      });

      onSuccess(data);
      reset();
    },
    onError: (error) => {
      console.error('Failed to create auth type:', error);
      toaster.create({
        title: 'Creation Failed',
        description: 'Failed to create authentication type. Please try again.',
        type: 'error',
      });
    },
  });

  const onSubmit = async (data: BasicAuthFormData) => {
    createLocationAuthType(data as CreateAuthTypeRequest);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <DialogRoot
      open={isOpen}
      onOpenChange={(details: { open: boolean }) =>
        !details.open && handleClose()
      }
      closeOnInteractOutside={false}
    >
      <DialogContent maxW="600px">
        <DialogHeader>
          <HStack gap={3}>
            <Icon as={FaLock} color="blue.500" />
            <DialogTitle>Create Basic Authentication</DialogTitle>
          </HStack>
        </DialogHeader>
        <DialogCloseTrigger />
        <DialogBody pb={6}>
          <Box as="form" onSubmit={handleSubmit(onSubmit)}>
            <Stack gap={6} align="stretch">
              {/* Basic Information */}
              <Box>
                <Text fontSize="md" fontWeight="medium" mb={4}>
                  Basic Information
                </Text>
                <Stack gap={4}>
                  <Controller
                    control={control}
                    name="name"
                    render={({ field }) => (
                      <Field.Root invalid={!!errors.name}>
                        <Field.Label>Authentication Name</Field.Label>
                        <Input
                          {...field}
                          placeholder="Basic Authentication"
                          _placeholder={{
                            color: 'gray.200',
                          }}
                        />
                        <Field.ErrorText>
                          {errors.name?.message}
                        </Field.ErrorText>
                      </Field.Root>
                    )}
                  />

                  <Controller
                    control={control}
                    name="description"
                    render={({ field }) => (
                      <Field.Root invalid={!!errors.description}>
                        <Field.Label>Description</Field.Label>
                        <Input
                          {...field}
                          placeholder="Standard Basic Auth using username and password"
                          _placeholder={{
                            color: 'gray.200',
                          }}
                        />
                        <Field.ErrorText>
                          {errors.description?.message}
                        </Field.ErrorText>
                      </Field.Root>
                    )}
                  />
                </Stack>
              </Box>

              <Divider />

              {/* Basic Authentication Credentials */}
              <Box>
                <Text fontSize="md" fontWeight="medium" mb={4}>
                  Authentication Credentials
                </Text>
                <Stack gap={4}>
                  <Controller
                    control={control}
                    name="username"
                    render={({ field }) => (
                      <Field.Root invalid={!!errors.username}>
                        <Field.Label>Username</Field.Label>
                        <Input
                          {...field}
                          placeholder="Enter username"
                          _placeholder={{
                            color: 'gray.200',
                          }}
                        />
                        <Field.ErrorText>
                          {errors.username?.message}
                        </Field.ErrorText>
                      </Field.Root>
                    )}
                  />

                  <Controller
                    control={control}
                    name="password"
                    render={({ field }) => (
                      <Field.Root invalid={!!errors.password}>
                        <Field.Label>Password</Field.Label>
                        <Box position="relative">
                          <Input
                            {...field}
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Your password"
                            _placeholder={{
                              color: 'gray.200',
                            }}
                            pr="12"
                          />
                          <Button
                            position="absolute"
                            right="2"
                            top="50%"
                            transform="translateY(-50%)"
                            size="sm"
                            variant="ghost"
                            onClick={() => setShowPassword(!showPassword)}
                            aria-label={
                              showPassword ? 'Hide password' : 'Show password'
                            }
                          >
                            <Icon as={showPassword ? FaEyeSlash : FaEye} />
                          </Button>
                        </Box>
                        <Field.ErrorText>
                          {errors.password?.message}
                        </Field.ErrorText>
                      </Field.Root>
                    )}
                  />
                </Stack>
              </Box>

              {/* Action Buttons */}
              <HStack justify="flex-end" gap={3} mt={6}>
                <Button
                  variant="outline"
                  size="md"
                  borderRadius="xl"
                  bg="gray.50"
                  borderColor="gray.300"
                  color="gray.600"
                  _hover={{
                    bg: 'gray.100',
                    borderColor: 'gray.400',
                    color: 'gray.700',
                  }}
                  onClick={handleClose}
                  disabled={isSubmitting}
                  px={6}
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  variant="solid"
                  size="md"
                  borderRadius="xl"
                  bg="blue.500"
                  color="white"
                  _hover={{
                    bg: 'blue.600',
                    transform: 'translateY(-1px)',
                    boxShadow: 'lg',
                  }}
                  _active={{
                    bg: 'blue.700',
                    transform: 'translateY(0)',
                  }}
                  transition="all 0.2s"
                  fontWeight="semibold"
                  loading={isSubmitting}
                  loadingText="Creating..."
                  px={6}
                >
                  Create
                </Button>
              </HStack>
            </Stack>
          </Box>
        </DialogBody>
      </DialogContent>
    </DialogRoot>
  );
};
